import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import AdminAuthGuard from 'utils/route-guard/AdminAuthGuard';
import AdminGuestGuard from 'utils/route-guard/AdminGuestGuard';
import ProtectedRoute from 'ui-component/ProtectedRoute';
import AdminLogin from 'views/AdminLogin';
import AgreementGrievance from 'views/dashboard/Settings/Agreement-Grievance';
import AccessDeniedPage from 'views/AccessDeniedPage';

const AccountClassification = Loadable(lazy(() => import('views/dashboard/Settings/Account-Classification')));
const BucketMaster = Loadable(lazy(() => import('views/dashboard/Settings/Bucket-Master')));
const DesignationMaster = Loadable(lazy(() => import('views/dashboard/Settings/Designation-Master')));

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const DashboardAnalytics = Loadable(lazy(() => import('views/dashboard/Analytics')));
const ServiceableAreaPincodeManagement = Loadable(lazy(() => import('views/dashboard/Settings/Serviceable-Area-Pincode')));
const UploadHistory = Loadable(lazy(() => import('views/dashboard/Settings/Upload-History')));
const LoanCategory = Loadable(lazy(() => import('views/dashboard/Settings/Loan-Category')));
const LoanSubcategory = Loadable(lazy(() => import('views/dashboard/Settings/Loan-Subcategory')));
const LanguageMaster = Loadable(lazy(() => import('views/dashboard/Settings/Language-Master')));
const TaxConfiguration = Loadable(lazy(() => import('views/dashboard/Settings/Tax-Configuration')));
const MenuMaster = Loadable(lazy(() => import('views/dashboard/Settings/Menu-Master')));
const Users = Loadable(lazy(() => import('views/dashboard/Settings/Users')));
const PaymentMethods = Loadable(lazy(() => import('views/dashboard/Settings/Payment-Settings/Payment-Methods')));
const AppVersionControl = Loadable(lazy(() => import('views/dashboard/Settings/App-Version-Control')));
const LoanApplicants = Loadable(lazy(() => import('views/dashboard/Loans/Loan-Applicant')));
const LoanMonitoring = Loadable(lazy(() => import('views/dashboard/Loans/Loan-Monitoring')));
const Disbursement = Loadable(lazy(() => import('views/dashboard/Loans/Disbursement')));
const PayoutSetting = Loadable(lazy(() => import('views/dashboard/Settings/Payout-Setting')));
const ManualKYCReview = Loadable(lazy(() => import('views/dashboard/Manual-KYC-Review/Review-Queue')));
const ManualKYCReviewDetail = Loadable(lazy(() => import('views/dashboard/Manual-KYC-Review/Applicant-Details')));
const LoanLedger = Loadable(lazy(() => import('views/dashboard/Ledger-Master/Loan-Ledger')));
const AccountLedger = Loadable(lazy(() => import('views/dashboard/Ledger-Master/Account-Ledger')));
const SMSMaster = Loadable(lazy(() => import('views/dashboard/Settings/SMS-Master')));
const MailTemplateList = Loadable(lazy(() => import('views/dashboard/Mail-Template')));
const BRERulesList = Loadable(lazy(() => import('views/dashboard/BRE/Rules')));
const BRETemplateList = Loadable(lazy(() => import('views/dashboard/BRE/Templates')));
const Error = Loadable(lazy(() => import('views/Error')));
// ==============================|| MAIN ROUTING ||============================== //

// Admin Login Route (protected with AdminGuestGuard - only accessible to non-authenticated users)
const AdminLoginRoute = {
  path: '/admin/login',
  element: (
    <AdminGuestGuard>
      <AdminLogin />
    </AdminGuestGuard>
  )
};

const MaintenanceErrorRoute = {
  path: '/*',
  element: <Error />
};

const accessDeniedRoute = {
  path: '/access-denied',
  element: <AccessDeniedPage />
};

// Main Dashboard Route (protected with AdminAuthGuard)
const MainRoutes = {
  path: '/',
  element: (
    <AdminAuthGuard>
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    </AdminAuthGuard>
  ),
  children: [
    {
      path: '/dashboard/default',
      element: <DashboardDefault />
    },
    {
      path: '/dashboard/analytics',
      element: <DashboardAnalytics />
    },
    {
      path: '/settings/serviceable-area-pincode',
      element: <ProtectedRoute component={ServiceableAreaPincodeManagement} moduleId="serviceable-area-pincode" requiredAction="read" />
    },
    {
      path: '/settings/upload-history',
      element: <ProtectedRoute component={UploadHistory} moduleId="serviceable-area-pincode" requiredAction="read" />
    },
    {
      path: '/settings/loan-category',
      element: <ProtectedRoute component={LoanCategory} moduleId="loan-category" requiredAction="read" />
    },
    {
      path: '/settings/loan-subcategory',
      element: <ProtectedRoute component={LoanSubcategory} moduleId="loan-subcategory" requiredAction="read" />
    },
    {
      path: '/settings/language-master',
      element: <ProtectedRoute component={LanguageMaster} moduleId="language-master" requiredAction="read" />
    },
    {
      path: '/settings/account-classification',
      element: <ProtectedRoute component={AccountClassification} moduleId="account-classification" requiredAction="read" />
    },
    {
      path: '/settings/bucket-master',
      element: <ProtectedRoute component={BucketMaster} moduleId="bucket-master" requiredAction="read" />
    },
    {
      path: '/settings/agreement-grievance',
      element: <ProtectedRoute component={AgreementGrievance} moduleId="agreement-grievance" requiredAction="read" />
    },
    {
      path: '/settings/designation-master',
      element: <ProtectedRoute component={DesignationMaster} moduleId="designation-master" requiredAction="read" />
    },
    {
      path: '/settings/tax-configuration',
      element: <ProtectedRoute component={TaxConfiguration} moduleId="tax-configuration" requiredAction="read" />
    },
    {
      path: '/settings/menu-master',
      element: <ProtectedRoute component={MenuMaster} moduleId="menu-master" requiredAction="read" />
    },
    {
      path: '/settings/users',
      element: <ProtectedRoute component={Users} moduleId="users" requiredAction="read" />
    },
    {
      path: '/settings/payment-methods',
      element: <ProtectedRoute component={PaymentMethods} moduleId="payment-methods" requiredAction="read" />
    },
    {
      path: '/loans/applicants',
      element: <ProtectedRoute component={LoanApplicants} moduleId="loan-applicants" requiredAction="read" />
    },
    {
      path: '/loans/monitoring',
      element: <ProtectedRoute component={LoanMonitoring} moduleId="loan-monitoring" requiredAction="read" />
    },
    {
      path: '/loans/disbursement',
      element: <ProtectedRoute component={Disbursement} moduleId="disbursement" requiredAction="read" />
    },
    {
      path: '/settings/payout-setting',
      element: <ProtectedRoute component={PayoutSetting} moduleId="payout-setting" requiredAction="read" />
    },
    {
      path: '/settings/app-version-control',
      element: <ProtectedRoute component={AppVersionControl} moduleId="app-version-control" requiredAction="read" />
    },
    {
      path: '/settings/sms-master',
      element: <ProtectedRoute component={SMSMaster} moduleId="sms-master" requiredAction="read" />
    },
    {
      path: '/manual-kyc-review/queue',
      element: <ProtectedRoute component={ManualKYCReview} moduleId="kyc-review-queue" requiredAction="read" />
    },
    {
      path: '/manual-kyc-review/:applicantId',
      element: <ProtectedRoute component={ManualKYCReviewDetail} moduleId="manual-kyc-review" requiredAction="read" />
    },
    {
      path: '/ledger-master/loan-ledger',
      element: <ProtectedRoute component={LoanLedger} moduleId="loan-ledger" requiredAction="read" />
    },
    {
      path: '/ledger-master/account-ledger',
      element: <ProtectedRoute component={AccountLedger} moduleId="account-ledger" requiredAction="read" />
    },
    {
      path: '/mail-template/list',
      element: <ProtectedRoute component={MailTemplateList} moduleId="mail-template-list" requiredAction="read" />
    },
    {
      path: '/bre-master/rules',
      element: <ProtectedRoute component={BRERulesList} moduleId="bre-master-rules" requiredAction="read" />
    },
    {
      path: '/bre-master/templates',
      element: <ProtectedRoute component={BRETemplateList} moduleId="bre-master-templates" requiredAction="read" />
    }
  ]
};

export default [AdminLoginRoute, MainRoutes, MaintenanceErrorRoute, accessDeniedRoute];
